import React, { useState, useEffect, useCallback } from 'react';
import {
  dpdStatus, dpdUebersicht, dpdRequestCode, dpdVerifyCode, dpdLogout,
  dpdAbgleich, dpdLog, dpdNotiz,
  isDpdOn, setDpdOn, isDpdAnnahmeOn, setDpdAnnahmeOn, isDpdAusgabeOn, setDpdAusgabeOn,
  offeneDpdPakete, rueckgabeMitFach, dpdT, vorladenStart,
} from '../services/dpd';
import { autoRegalFach } from '../services/regalUnterfach';
import { isAutoRegalLetter } from '../services/printerService';

export default function DpdPanel({
  colors = {},
  language = 'de',
  pakete = [],
  onNamen,
  onAbgeholt,
  onAnlegen,
  onToggle,
  onClose,
}) {
  const txt = dpdT(language);
  const isNl = String(language || '').startsWith('nl');
  const isEn = String(language || '').startsWith('en');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uebersicht, setUebersicht] = useState(null);
  const [uebLoading, setUebLoading] = useState(false);

  // Login-Zustand (2-Schritt OTP)
  const [pudoId, setPudoId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [authMsg, setAuthMsg] = useState(null);

  // Geräte-Schalter
  const [aktiv, setAktiv] = useState(() => isDpdOn());
  const [liveAnnahme, setLiveAnnahme] = useState(() => isDpdAnnahmeOn());
  const [liveAusgabe, setLiveAusgabe] = useState(() => isDpdAusgabeOn());

  // Abgleich
  const [abgleich, setAbgleich] = useState(null);
  const [abgleichBusy, setAbgleichBusy] = useState(false);
  const [aktionBusy, setAktionBusy] = useState(false);

  // Unterlisten ein-/ausklappen
  const [zeigeAngekuendigt, setZeigeAngekuendigt] = useState(false);
  const [zeigeProtokoll, setZeigeProtokoll] = useState(false);
  const [logEntries, setLogEntries] = useState([]);

  const ladeAlles = useCallback(async () => {
    setLoading(true);
    try {
      const s = await dpdStatus();
      setStatus(s);
      if (s && s.pudoId) setPudoId(s.pudoId);
      if (s && s.angemeldet) {
        setUebLoading(true);
        const u = await dpdUebersicht();
        setUebersicht(u);
        setUebLoading(false);
      } else {
        setUebersicht(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    ladeAlles();
  }, [ladeAlles]);

  const ladeProtokoll = useCallback(async () => {
    const r = await dpdLog(60);
    if (r && Array.isArray(r.entries)) setLogEntries(r.entries);
  }, []);

  useEffect(() => {
    if (zeigeProtokoll) ladeProtokoll();
  }, [zeigeProtokoll, ladeProtokoll]);

  const handleRequestCode = async (e) => {
    if (e) e.preventDefault();
    if (!pudoId.trim()) return;
    setAuthBusy(true);
    setRequestBusy(true);
    setAuthMsg(null);
    try {
      const r = await dpdRequestCode(pudoId);
      if (r && r.ok) {
        setCodeSent(true);
        const mailHint = r.mail ? ` (${r.mail})` : '';
        setAuthMsg({ ok: true, text: `${txt.codeGesendet}${mailHint}` });
      } else {
        setAuthMsg({ ok: false, text: (r && r.message) || 'Fehler beim Anfordern des Codes' });
      }
    } finally {
      setAuthBusy(false);
      setRequestBusy(false);
    }
  };

  const handleVerifyCode = async (e) => {
    if (e) e.preventDefault();
    if (!pudoId.trim() || !otpCode.trim()) return;
    setAuthBusy(true);
    setVerifyBusy(true);
    setAuthMsg(null);
    try {
      const r = await dpdVerifyCode(pudoId, otpCode);
      if (r && r.ok) {
        setOtpCode('');
        setCodeSent(false);
        setAuthMsg({ ok: true, text: isNl ? 'Succesvol verbonden met DPD Pickup!' : isEn ? 'Successfully connected to DPD Pickup!' : 'Erfolgreich mit DPD Pickup verbunden!' });
        if (!isDpdOn()) {
          setDpdOn(true);
          setAktiv(true);
          if (onToggle) onToggle();
        }
        await ladeAlles();
        vorladenStart(true).catch(() => {});
      } else {
        setAuthMsg({ ok: false, text: (r && r.message) || 'Code ungültig oder abgelaufen' });
      }
    } finally {
      setAuthBusy(false);
      setVerifyBusy(false);
    }
  };

  const handleLogout = async () => {
    const frage = isNl
      ? 'DPD Pickup-koppeling echt afmelden?'
      : isEn
        ? 'Really disconnect DPD Pickup?'
        : 'DPD Pickup-Verbindung wirklich abmelden?';
    if (!window.confirm(frage)) return;
    setAuthBusy(true);
    try {
      await dpdLogout();
      await ladeAlles();
    } finally {
      setAuthBusy(false);
    }
  };

  const toggleAktiv = (checked) => {
    setDpdOn(checked);
    setAktiv(checked);
    if (checked) vorladenStart(true).catch(() => {});
    if (onToggle) onToggle();
  };

  const toggleLiveAnnahme = (checked) => {
    if (checked) {
      const warn = isNl
        ? 'Live-inname inschakelen? Elk gescand DPD-pakket wordt direct als ontvangen (DELIVERY) bij DPD Pickup geboekt.'
        : isEn
          ? 'Enable live intake? Every scanned DPD parcel will be booked directly as received (DELIVERY) in DPD Pickup.'
          : 'Live-Annahme einschalten? Jedes gescannte DPD-Paket wird sofort als angenommen (DELIVERY) bei DPD Pickup gebucht.';
      if (!window.confirm(warn)) return;
    }
    setDpdAnnahmeOn(checked);
    setLiveAnnahme(checked);
    if (onToggle) onToggle();
  };

  const toggleLiveAusgabe = (checked) => {
    if (checked) {
      const warn = isNl
        ? 'Live-uitgifte inschakelen? Uitgifte wordt direct bij DPD Pickup geboekt.'
        : isEn
          ? 'Enable live pickup? Customer pickups will be booked directly in DPD Pickup.'
          : 'Live-Ausgabe einschalten? Ausgaben werden direkt bei DPD Pickup gebucht.';
      if (!window.confirm(warn)) return;
    }
    setDpdAusgabeOn(checked);
    setLiveAusgabe(checked);
    if (onToggle) onToggle();
  };

  const starteAbgleich = async () => {
    setAbgleichBusy(true);
    try {
      const eigene = offeneDpdPakete(pakete);
      const r = await dpdAbgleich(eigene);
      setAbgleich(r);
    } finally {
      setAbgleichBusy(false);
    }
  };

  const alleFehlendenAnlegen = async (liste) => {
    if (!onAnlegen || !Array.isArray(liste) || !liste.length) return;
    setAktionBusy(true);
    try {
      const autoLetter = isAutoRegalLetter();
      for (const item of liste) {
        const name = item.name || `DPD ${String(item.code || '').slice(-6)}`;
        const fach = autoLetter && item.name ? autoRegalFach(item.name) : '';
        await onAnlegen({
          tracking: item.code,
          name,
          regalplatz: fach,
          dienst: 'DPD',
          drucken: false,
        });
      }
      await dpdNotiz('uebernahme', `${liste.length} angelegt`, true, '', 'Aus DPD-Bestandsabgleich angelegt');
      await starteAbgleich();
    } finally {
      setAktionBusy(false);
    }
  };

  const alleAbgeholtenMarkieren = async (liste) => {
    if (!onAbgeholt || !Array.isArray(liste) || !liste.length) return;
    const ids = liste.map((x) => x.id).filter(Boolean);
    if (!ids.length) return;
    const frage = isNl
      ? `${ids.length} pakket(ten) die bij DPD al zijn opgehaald ook in PaketPilot als opgehaald markeren?`
      : isEn
        ? `Mark ${ids.length} parcel(s) already picked up in DPD as picked up in PaketPilot too?`
        : `${ids.length} Paket(e), die bei DPD bereits abgeholt sind, auch in PaketPilot als ausgegeben markieren?`;
    if (!window.confirm(frage)) return;
    setAktionBusy(true);
    try {
      await onAbgeholt(ids);
      await dpdNotiz('uebernahme', `${ids.length} ausgetragen`, true, '', 'Aus DPD-Bestandsabgleich als abgeholt markiert');
      await starteAbgleich();
    } finally {
      setAktionBusy(false);
    }
  };

  const alleNamenUebernehmen = async (liste) => {
    if (!onNamen || !Array.isArray(liste) || !liste.length) return;
    setAktionBusy(true);
    try {
      const autoLetter = isAutoRegalLetter();
      const updates = [];
      for (const item of liste) {
        const p = (pakete || []).find((x) => x && x.id === item.id);
        if (!p || !item.name) continue;
        const fach = (!p.regalplatz || p.regalplatz === '?') && autoLetter ? autoRegalFach(item.name) : undefined;
        updates.push({ paket: p, code: item.code, name: item.name, ...(fach ? { regalplatz: fach } : {}) });
      }
      if (updates.length) {
        await onNamen(updates);
        await dpdNotiz('uebernahme', `${updates.length} Namen`, true, '', 'Namen aus DPD Pickup übernommen');
      }
      await starteAbgleich();
    } finally {
      setAktionBusy(false);
    }
  };

  const bgCard = colors.darkMode ? '#22272e' : '#f6f8fa';
  const borderCol = colors.border || (colors.darkMode ? '#373e47' : '#d0d7de');
  const rueckgabeListe = uebersicht && uebersicht.ok ? rueckgabeMitFach(uebersicht.rueckgabe || [], pakete) : [];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 14,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
    >
      <div
        style={{
          background: colors.box || (colors.darkMode ? '#1c2128' : '#ffffff'),
          color: colors.text || (colors.darkMode ? '#adbac7' : '#1f2328'),
          borderRadius: 14,
          maxWidth: 620,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '18px 20px',
          boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
          border: `1px solid ${borderCol}`,
        }}
      >
        {/* Kopfzeile */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📦 {txt.titel}</h2>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{txt.untertitel}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              color: colors.text,
              fontSize: 20,
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Verbindungsstatus */}
        <div style={{ background: bgCard, border: `1px solid ${borderCol}`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {loading
                  ? '⏳ …'
                  : status && status.angemeldet
                    ? `🟢 ${txt.verbunden} (${status.pudoId || pudoId})`
                    : `⚪ ${txt.nichtVerbunden}`}
              </div>
              {status && status.shopName && (
                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                  🏪 {status.shopName}{status.shoport ? ` · ${status.shoport}` : ''}
                </div>
              )}
              {status && status.letzterFehler && (
                <div style={{ fontSize: 12, color: '#e5534b', marginTop: 4 }}>
                  ⚠️ {status.letzterFehler}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={ladeAlles}
                disabled={loading}
                style={{
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: `1px solid ${borderCol}`,
                  background: colors.darkMode ? '#2d333b' : '#ffffff',
                  color: colors.text,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                🔄 {txt.neuPruefen}
              </button>
              {status && status.angemeldet && (
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={authBusy}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: `1px solid ${borderCol}`,
                    background: 'transparent',
                    color: '#e5534b',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {txt.abmelden}
                </button>
              )}
            </div>
          </div>

          {/* 2-Schritt-Anmeldung per Shop-ID + E-Mail-Code */}
          {(!status || !status.angemeldet) && !loading && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${borderCol}` }}>
              <form onSubmit={handleRequestCode} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{txt.pudoIdLabel}</label>
                  <input
                    type="text"
                    value={pudoId}
                    onChange={(e) => setPudoId(e.target.value.toUpperCase())}
                    placeholder="NL24808"
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: 6,
                      border: `1px solid ${borderCol}`,
                      background: colors.darkMode ? '#1c2128' : '#ffffff',
                      color: colors.text,
                      fontSize: 14,
                      fontWeight: 600,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={authBusy || !pudoId.trim()}
                  style={{
                    padding: '9px 14px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#dc0032',
                    color: '#ffffff',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  📧 {requestBusy ? txt.codeSenden : txt.codeAnfordern}
                </button>
              </form>

              <form onSubmit={handleVerifyCode} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{txt.codeLabel}</label>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.toUpperCase())}
                    placeholder="z. B. Y43BS7"
                    maxLength={10}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: 6,
                      border: codeSent ? '2px solid #2da44e' : `1px solid ${borderCol}`,
                      background: colors.darkMode ? '#1c2128' : '#ffffff',
                      color: colors.text,
                      fontSize: 15,
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={authBusy || !otpCode.trim()}
                  style={{
                    padding: '9px 14px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#2da44e',
                    color: '#ffffff',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  🔑 {verifyBusy ? txt.verbinde : txt.codeBestaetigen}
                </button>
              </form>
            </div>
          )}

          {authMsg && (
            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: authMsg.ok ? '#2da44e' : '#e5534b' }}>
              {authMsg.ok ? '✅ ' : '⚠️ '}{authMsg.text}
            </div>
          )}
        </div>

        {/* Geräte-Schalter */}
        <div style={{ background: bgCard, border: `1px solid ${borderCol}`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={aktiv}
              onChange={(e) => toggleAktiv(e.target.checked)}
              style={{ width: 18, height: 18, marginTop: 2 }}
            />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{txt.aufGeraetAktiv}</div>
              <div style={{ fontSize: 12, opacity: 0.78, marginTop: 2, lineHeight: 1.4 }}>{txt.nurLesenHinweis}</div>
            </div>
          </label>

          {aktiv && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${borderCol}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={liveAnnahme}
                  onChange={(e) => toggleLiveAnnahme(e.target.checked)}
                  style={{ width: 17, height: 17 }}
                />
                <span style={{ fontSize: 13, fontWeight: 600 }}>📥 {txt.liveAnnahme}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={liveAusgabe}
                  onChange={(e) => toggleLiveAusgabe(e.target.checked)}
                  style={{ width: 17, height: 17 }}
                />
                <span style={{ fontSize: 13, fontWeight: 600 }}>📤 {txt.liveAusgabe}</span>
              </label>
            </div>
          )}
        </div>

        {/* Tagesübersicht aus DPD Pickup */}
        {status && status.angemeldet && (
          <div style={{ background: bgCard, border: `1px solid ${borderCol}`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
              📊 {txt.heuteTitel} {uebLoading && <span style={{ fontWeight: 400, fontSize: 12 }}>⏳</span>}
            </div>
            {uebersicht && uebersicht.ok && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  <div style={{ padding: 10, borderRadius: 8, background: colors.darkMode ? '#1c2128' : '#ffffff', border: `1px solid ${borderCol}`, textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800 }}>{uebersicht.imShop ?? 0}</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>{txt.imShop}</div>
                  </div>
                  <div
                    onClick={() => setZeigeAngekuendigt((v) => !v)}
                    style={{ padding: 10, borderRadius: 8, background: colors.darkMode ? '#1c2128' : '#ffffff', border: `1px solid ${borderCol}`, textAlign: 'center', cursor: 'pointer' }}
                  >
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#2f81f7' }}>{uebersicht.unterwegs ?? 0}</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>{txt.unterwegs} ▾</div>
                  </div>
                  <div style={{ padding: 10, borderRadius: 8, background: colors.darkMode ? '#1c2128' : '#ffffff', border: `1px solid ${borderCol}`, textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: (uebersicht.rueckgabeAnzahl || 0) > 0 ? '#d29922' : undefined }}>
                      {uebersicht.rueckgabeAnzahl ?? 0}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>{txt.rueckgabe}</div>
                  </div>
                </div>

                {/* Rückgabe-Pakete mit Regalfach */}
                {rueckgabeListe.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>↩️ {txt.rueckgabe}:</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflowY: 'auto' }}>
                      {rueckgabeListe.map((r) => (
                        <div key={r.code} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 8px', borderRadius: 6, background: colors.darkMode ? '#1c2128' : '#ffffff' }}>
                          <span><strong>{r.name || '—'}</strong> · <code style={{ fontSize: 11 }}>{r.code}</code></span>
                          {r.fach && <span style={{ fontWeight: 700, color: '#d29922' }}>Fach {r.fach}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Unterwegs / angekündigte Pakete (Vorab-Namen!) */}
                {zeigeAngekuendigt && Array.isArray(uebersicht.angekuendigt) && uebersicht.angekuendigt.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                      🚚 {txt.unterwegs} ({uebersicht.angekuendigt.length}):
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
                      {uebersicht.angekuendigt.map((p) => (
                        <div key={p.code} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 8px', borderRadius: 6, background: colors.darkMode ? '#1c2128' : '#ffffff' }}>
                          <span><strong>{p.name || '—'}</strong></span>
                          <code style={{ fontSize: 11, opacity: 0.8 }}>{p.code}</code>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Bestands-Abgleich PaketPilot ↔ DPD Pickup */}
        {status && status.angemeldet && (
          <div style={{ background: bgCard, border: `1px solid ${borderCol}`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <button
              type="button"
              onClick={starteAbgleich}
              disabled={abgleichBusy || aktionBusy}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: 8,
                border: `1px solid ${borderCol}`,
                background: colors.darkMode ? '#2d333b' : '#ffffff',
                color: colors.text,
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              🔄 {abgleichBusy ? txt.abgleichLaeuft : txt.abgleichStarten}
            </button>

            {abgleich && abgleich.ok && (
              <div style={{ marginTop: 10, fontSize: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  DPD Pickup: <strong>{abgleich.dpdGesamt}</strong> · PaketPilot: <strong>{abgleich.paketpilotGesamt}</strong> · Übereinstimmend: <strong>{abgleich.stimmtUeberein}</strong>
                </div>

                {/* Fehlt in PaketPilot */}
                {Array.isArray(abgleich.nurInDpd) && abgleich.nurInDpd.length > 0 && (
                  <div style={{ marginTop: 8, padding: 8, borderRadius: 6, background: colors.darkMode ? '#1c2128' : '#ffffff', border: `1px solid ${borderCol}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <strong>➕ Nur in DPD Pickup ({abgleich.nurInDpd.length})</strong>
                      {onAnlegen && (
                        <button
                          type="button"
                          disabled={aktionBusy}
                          onClick={() => alleFehlendenAnlegen(abgleich.nurInDpd)}
                          style={{ padding: '4px 8px', borderRadius: 5, border: 'none', background: '#2da44e', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                        >
                          + Alle in PaketPilot anlegen
                        </button>
                      )}
                    </div>
                    <div style={{ maxHeight: 130, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {abgleich.nurInDpd.map((p) => (
                        <div key={p.code} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span>{p.name || '—'}</span>
                          <code>{p.code}</code>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Nur in PaketPilot (bei DPD schon abgeholt) */}
                {Array.isArray(abgleich.nurInPaketPilot) && abgleich.nurInPaketPilot.length > 0 && (
                  <div style={{ marginTop: 8, padding: 8, borderRadius: 6, background: colors.darkMode ? '#1c2128' : '#ffffff', border: `1px solid ${borderCol}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <strong>✓ Bei DPD nicht mehr im Bestand ({abgleich.nurInPaketPilot.length})</strong>
                      {onAbgeholt && (
                        <button
                          type="button"
                          disabled={aktionBusy}
                          onClick={() => alleAbgeholtenMarkieren(abgleich.nurInPaketPilot)}
                          style={{ padding: '4px 8px', borderRadius: 5, border: 'none', background: '#2f81f7', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                        >
                          ✓ Als abgeholt markieren
                        </button>
                      )}
                    </div>
                    <div style={{ maxHeight: 130, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {abgleich.nurInPaketPilot.map((p) => (
                        <div key={p.code} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span>{p.name || '—'} {p.fach ? `(Fach ${p.fach})` : ''}</span>
                          <code>{p.code}</code>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Name ergänzen / aktualisieren */}
                {Array.isArray(abgleich.nameFehltOderAnders) && abgleich.nameFehltOderAnders.length > 0 && (
                  <div style={{ marginTop: 8, padding: 8, borderRadius: 6, background: colors.darkMode ? '#1c2128' : '#ffffff', border: `1px solid ${borderCol}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <strong>✏️ Name aus DPD verfügbar ({abgleich.nameFehltOderAnders.length})</strong>
                      {onNamen && (
                        <button
                          type="button"
                          disabled={aktionBusy}
                          onClick={() => alleNamenUebernehmen(abgleich.nameFehltOderAnders)}
                          style={{ padding: '4px 8px', borderRadius: 5, border: 'none', background: '#8250df', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                        >
                          Namen übernehmen
                        </button>
                      )}
                    </div>
                    <div style={{ maxHeight: 130, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {abgleich.nameFehltOderAnders.map((p) => (
                        <div key={p.code} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span>{p.bisherName || '—'} → <strong>{p.name}</strong></span>
                          <code>{p.code}</code>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Protokoll */}
        <div style={{ background: bgCard, border: `1px solid ${borderCol}`, borderRadius: 10, padding: 12 }}>
          <div
            onClick={() => setZeigeProtokoll((v) => !v)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
          >
            <span>📋 {txt.protokollTitel}</span>
            <span>{zeigeProtokoll ? '▴' : '▾'}</span>
          </div>
          {zeigeProtokoll && (
            <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {logEntries.length === 0 ? (
                <div style={{ fontSize: 12, opacity: 0.7 }}>Noch keine Einträge</div>
              ) : (
                logEntries.map((e) => (
                  <div key={e.id} style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0', borderBottom: `1px solid ${borderCol}` }}>
                    <span>
                      {e.ok ? '✅' : '⚠️'} <strong>{e.op}</strong> {e.code ? `(${e.code})` : ''} {e.message ? `— ${e.message}` : ''}
                    </span>
                    <span style={{ opacity: 0.7, whiteSpace: 'nowrap' }}>
                      {e.ms ? `${e.ms} ms · ` : ''}{e.created_at ? new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

